import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../lib/supabase'
import type { User } from '@supabase/supabase-js'

export type UserRole = 'admin_global' | 'admin_secretaire' | 'commercial' | 'groupe_user' | 'facility_user'

interface UserProfile {
  id: string
  role: UserRole
  full_name: string | null
  avatar_url: string | null
  default_language: string | null
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const profile = ref<UserProfile | null>(null)
  const loading = ref(true)

  const isAuthenticated = computed(() => !!user.value)
  const userRole = computed(() => profile.value?.role || null)

  const isAdminGlobal = computed(() => userRole.value === 'admin_global')
  const isAdminSecretaire = computed(() => userRole.value === 'admin_secretaire')
  const isCommercial = computed(() => userRole.value === 'commercial')
  const isGroupeUser = computed(() => userRole.value === 'groupe_user')
  const isFacilityUser = computed(() => userRole.value === 'facility_user')

  async function loadUser() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        user.value = session.user
        await loadProfile()
      }
    } catch (error) {
      console.error('Error loading user:', error)
    } finally {
      loading.value = false
    }
  }

  async function loadProfile() {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select()
        .eq('id', user.value?.id)
        .single()

      if (error) throw error
      profile.value = data
    } catch (error) {
      console.error('Error loading profile:', error)
    }
  }

  async function signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) throw error

    user.value = data.user
    await loadProfile()

    return getRedirectPath()
  }

  async function signOut() {
    await supabase.auth.signOut()
    user.value = null
    profile.value = null
  }

  function getRedirectPath(): string {
    switch (profile.value?.role) {
      case 'admin_global':
        return '/adminConfig'
      case 'admin_secretaire':
        return '/adminGestion'
      case 'commercial':
        return '/commercial'
      case 'groupe_user':
        return '/group'
      case 'facility_user':
        return '/facility'
      default:
        return '/'
    }
  }

  return {
    user,
    profile,
    loading,
    isAuthenticated,
    userRole,
    isAdminGlobal,
    isAdminSecretaire,
    isCommercial,
    isGroupeUser,
    isFacilityUser,
    loadUser,
    signIn,
    signOut,
    getRedirectPath
  }
})